import {
  API_INVALID_IP,
  API_INVALID_SN,
  API_MISSING_CREDENTIALS,
  API_NOTHING_TO_DO,
  type NodeSn,
  type PublicVendorApi,
  type SnapshotId,
  type TableRequestBody,
  type TableRequestColumns,
  type TableResponseBody,
  isNonNullable,
  isNullable,
} from '@ipfabric/utils';
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { postTablesSnapshotDevices } from '@ipfabric/api-client/client/model/snapshots/snapshots';
import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { type FormikProps, useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useApi } from 'utils/request/useApi';

import type { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { transformVendorsForRequest } from 'app/features/DiscoverySettingsForms/components/VendorsApi/helpers/transformVendorsForRequest';
import type { ExistingVendor } from 'app/features/DiscoverySettingsForms/components/VendorsApi/vendorTypes';
import { useLocale } from 'containers/App/LocaleContext';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { openSettings } from 'pages/SnapshotManagement/actions';
import { useNotifications } from 'utils/hooks/useNotifications';
import type { InsertNetworkFormValues } from './SnapshotCreationAddDevices';
import type {
  ContinueDiscoveryEvent,
  SnapshotCreationDeviceSettingsRowData,
  SourceSelectEvent,
} from './SnapshotCreationDeviceSettings';
import {
  type SnapshotCreationRowData,
  type SnapshotCreationTableRequestBody,
  type TaskApiSourceMap,
  type VendorsSettingsId,
  snapshotCreationApiEndpoint,
} from './SnapshotCreationTable';

export type SnapshotCreationTabId =
  | 'addDevices'
  | 'createSnapshot'
  | 'refreshDevices'
  | 'deleteDevices';

type VendorSettingsMap = {
  [key: NodeSn]: VendorsSettingsId;
};

// strings in errors attributes represent IP addresses of errored devices
interface RequestIpErrors {
  type: 'ip';
  errors: {
    excluded?: string[];
    noCredentials?: string[];
    notIncluded?: string[];
  };
}

interface RequestSnErrors {
  type: 'sn';
  errors: {
    noApiSettings?: NodeSn[];
    noCredentials?: NodeSn[];
    unreachableIp?: NodeSn[];
    missingSns?: NodeSn[];
  };
}

export type RequestErrors = RequestIpErrors | RequestSnErrors;

type SnapshotCreationDependency = Pick<
  SnapshotCreationRowData,
  'id' | 'parentId' | 'sn' | 'hostname' | 'type'
>;

export const isRequestIpErrors = (
  requestErrors: RequestErrors,
): requestErrors is RequestIpErrors => requestErrors.type === 'ip';

export const isRequestSnErrors = (
  requestErrors: RequestErrors,
): requestErrors is RequestSnErrors => requestErrors.type === 'sn';

export interface SnapshotCreationHook {
  breadcrumbsStep: number;
  dataCount?: number;
  insertNetworkFormik: FormikProps<InsertNetworkFormValues>;
  loadingVendorsState: LoadingState;
  failedData: SnapshotCreationDeviceSettingsRowData[];
  loadDependencies: (
    nodeSns: NodeSn[],
    isDelete: boolean,
  ) => Promise<SnapshotCreationDependency[]>;
  onBreadcrumbsStep1Click: () => void;
  onContinueDiscovery: (event: ContinueDiscoveryEvent) => void;
  onCreateNewSnapshot: (selectedDeviceSns: NodeSn[]) => Promise<void>;
  onDeleteDevices: (selectedDeviceSns: NodeSn[]) => Promise<void>;
  onFetchVendors: () => void;
  onInsertNetwork: () => void;
  onRefreshDevices: (selectedDeviceSns: NodeSn[]) => Promise<void>;
  onSnapshotSettingsClose: () => void;
  onSnapshotSettingsOpen: (settingsTab: string) => void;
  onSourceSelect: (event: SourceSelectEvent) => void;
  onTabChange: (tab: SnapshotCreationTabId) => void;
  requestErrors: RequestErrors | null;
  selectedSns: NodeSn[];
  sendingDataState: LoadingState;
  snapshotSettingsTab: string | null;
  tabId: SnapshotCreationTabId;
  taskApiSourceMap: TaskApiSourceMap | null;
  vendors: ExistingVendor[];
  setVendors: Dispatch<SetStateAction<ExistingVendor[]>>;
}

interface Props {
  defaultTabId: SnapshotCreationTabId;
  onClose?: () => void;
  preselectedSns: NodeSn[];
  snapshotId: SnapshotId | null;
}

const getRequestFailedSns = (e: IpfError<'API'>): NodeSn[] => {
  const data = e.payload?.response?.data as any;
  return isNonNullable(data)
    ? pipe(
        isNonNullable(data.noApiSettings) ? data.noApiSettings : [],
        (sns) =>
          isNonNullable(data.noCredentials)
            ? [...sns, ...data.noCredentials]
            : sns,
        (sns) =>
          isNonNullable(data.unreachableIp)
            ? [...sns, ...data.unreachableIp]
            : sns,
      )
    : [];
};

const requestColumns: TableRequestColumns<SnapshotCreationRowData> = [
  'id',
  'isApiTask',
  'isSelected',
  'parentId',
  'sn',
  'source',
  'hostname',
  'vendor',
  'family',
  'settingsStates',
];

const discoveryErrors = [
  API_MISSING_CREDENTIALS,
  API_INVALID_IP,
  API_INVALID_SN,
];

export const useSnapshotCreation = (props: Props): SnapshotCreationHook => {
  const msg = useLocale();
  const api = useApi();
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();
  const [vendors, setVendors] = useState<ExistingVendor[]>([]);
  // pass selected device sns of discovery operation between steps (retry at step 2)
  const [selectedSns, setSelectedSns] = useState<NodeSn[]>([]);
  const [breadcrumbsStep, setBreadcrumbsStep] = useState<number>(1);
  const [tabId, setTabId] = useState<SnapshotCreationTabId>(props.defaultTabId);
  const [taskApiSourceMap, setTaskApiSourceMap] =
    useState<TaskApiSourceMap | null>(null);
  const [failedData, setFailedData] = useState<
    SnapshotCreationDeviceSettingsRowData[]
  >([]);
  const [requestErrors, setRequestErrors] = useState<RequestErrors | null>(
    null,
  );
  const [vendorSettingsMap, setVendorSettingsMap] = useState<VendorSettingsMap>(
    {},
  );
  const [snapshotSettingsTab, setSnapshotSettingsTab] = useState<string | null>(
    null,
  );
  const [sendingDataState, setSendingDataState] =
    useState<LoadingState>('INITIAL');
  const [loadingVendorsState, setLoadingVendorsState] =
    useState<LoadingState>('INITIAL');

  // biome-ignore lint/correctness/useExhaustiveDependencies: run on snapshot change
  useEffect(() => {
    setBreadcrumbsStep(1);
    setSelectedSns(props.preselectedSns);
  }, [props.snapshotId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: run on tab change
  useEffect(() => {
    setBreadcrumbsStep(1);
  }, [tabId]);

  const onTabChange = useStableCallback((tab: SnapshotCreationTabId) => {
    setTabId(tab);
  });

  const insertNetworkFormik = useFormik<InsertNetworkFormValues>({
    initialValues: {
      ipList: { ips: [] },
      retryTimedOut: true,
    },
    onSubmit: async () => {},
  });

  const loadDependencies = useStableCallback(
    async (nodeSns: NodeSn[], isDelete: boolean) => {
      const result = await api.request<
        TableResponseBody<SnapshotCreationRowData>,
        TableRequestBody<SnapshotCreationRowData>
      >({
        url: '/tables/snapshot-devices',
        method: 'POST',
        data: {
          columns: ['sn', 'id', 'parentId', 'hostname', 'type'],
          filters: {
            isSelected: ['eq', true],
          },
          bindVariables: {
            selected: nodeSns,
            isDelete,
          },
          snapshot: props.snapshotId!,
        },
      });
      return result.data;
    },
  );

  const onSourceSelect = useStableCallback((event: SourceSelectEvent) => {
    setVendorSettingsMap({
      ...vendorSettingsMap,
      [event.deviceSn]: event.settingsId,
    });
    setFailedData(
      failedData.map((row) =>
        row.sn === event.deviceSn
          ? {
              ...row,
              settingsStates: ['ok'],
            }
          : row,
      ),
    );
  });

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  const showFixSettings = useStableCallback(async (e: IpfError<'API'>) => {
    if (props.snapshotId === null) {
      return;
    }
    // If snapshot settings is saved and there are still errors,
    // close snapshot settings to show user new errors.
    if (snapshotSettingsTab !== null) {
      onSnapshotSettingsClose();
      showNotification({
        type: 'danger',
        message: e.message,
      });
    }
    const responseErrorData = e.payload?.response?.data as any;

    // Not all required node SNs are selected, something happened in UI. API can't continue.
    if (
      responseErrorData.nonExistingSns !== undefined &&
      responseErrorData.nonExistingSns.length > 0
    ) {
      throw e;
    }

    if (isNonNullable(responseErrorData)) {
      setRequestErrors({
        type: tabId === 'addDevices' ? 'ip' : 'sn',
        errors: responseErrorData,
      });
    }
    const requestSns = getRequestFailedSns(e);
    const [vendorSettings, response] = await Promise.all([
      responseErrorData.noApiSettings !== undefined
        ? api.request<TaskApiSourceMap>({
            url: `/snapshots/${props.snapshotId}/available-vendor-settings-per-type`,
            method: 'GET',
          })
        : null,
      requestSns.length
        ? api.request<
            TableResponseBody<SnapshotCreationDeviceSettingsRowData>,
            SnapshotCreationTableRequestBody
          >({
            url: snapshotCreationApiEndpoint,
            method: 'POST',
            data: {
              bindVariables: { selected: [] },
              columns: requestColumns,
              filters: {
                or: requestSns.map((sn) => ({ sn: ['eq', sn] })),
              },
              snapshot: props.snapshotId,
            },
          })
        : null,
    ]);
    setBreadcrumbsStep(2);
    if (vendorSettings !== null) {
      setTaskApiSourceMap(vendorSettings);
    }
    setFailedData(response?.data ?? []);
  });

  const onStartDiscovery = async (
    url: string,
    selectedDeviceSns?: NodeSn[],
    continueDiscoveryEvent?: ContinueDiscoveryEvent,
    vendorSettingsMapOverride?: VendorSettingsMap,
  ) => {
    const currentSnList = pipe(
      selectedDeviceSns ?? selectedSns,
      continueDiscoveryEvent?.acceptedDeviceSns === undefined
        ? array.filter(
            (sn) =>
              !(continueDiscoveryEvent?.notSelectedDeviceSns ?? []).includes(
                sn,
              ),
          )
        : array.concat(continueDiscoveryEvent.acceptedDeviceSns),
    );
    // update selected sns to be used in retry if discovery fails
    setSelectedSns(currentSnList);
    await withLoadingIndicator(setSendingDataState)(async () => {
      try {
        await api.request({
          url,
          method: 'POST',
          data: {
            snList: currentSnList,
            vendorSettingsMap: vendorSettingsMapOverride ?? vendorSettingsMap,
          },
        });
      } catch (e: any) {
        if (discoveryErrors.includes(e.code)) {
          await showFixSettings(e);
          return;
        }
        throw e;
      }
      setVendorSettingsMap({});
      props.onClose?.();
    });
  };

  const onCreateNewSnapshot = useStableCallback(
    async (
      selectedDeviceSns: NodeSn[],
      continueDiscoveryEvent?: ContinueDiscoveryEvent,
    ) => {
      await onStartDiscovery(
        `/snapshots/${props.snapshotId}/subset`,
        selectedDeviceSns,
        continueDiscoveryEvent,
      );
    },
  );

  const shouldReuseVendorSettingsMap = (
    snapshotId: SnapshotId | null,
    selectedDeviceSns: NodeSn[],
  ) =>
    // No snapshot means we cannot look up vendor settings.
    snapshotId === null ||
    // The map is already populated by explicit user selections.
    Object.keys(vendorSettingsMap).length > 0 ||
    // Nothing selected means no inputs for enrichment.
    selectedDeviceSns.length === 0;

  const resolveVendorSettingsMap = useStableCallback(
    async (selectedDeviceSns: NodeSn[]): Promise<VendorSettingsMap> => {
      const snapshotId = props.snapshotId;
      if (shouldReuseVendorSettingsMap(snapshotId, selectedDeviceSns)) {
        return vendorSettingsMap;
      }

      // Load the selected devices and keep only those that use vendor API
      // settings; non-API tasks don't need vendorSettingsMap entries.
      const devicesResponse = await postTablesSnapshotDevices({
        bindVariables: { selected: [] },
        columns: ['sn', 'source'],
        filters: {
          and: [
            { or: selectedDeviceSns.map((sn) => ({ sn: ['eq', sn] })) },
            { isApiTask: ['eq', true] },
          ],
        },
        snapshot: snapshotId!,
      });

      if (devicesResponse.data.length === 0) {
        return vendorSettingsMap;
      }

      const snapshotSettings = await api.request<{
        vendorApi?: PublicVendorApi[];
      }>({
        url: `/snapshots/${snapshotId}/settings`,
        method: 'GET',
      });

      // Load snapshot vendor API settings and index enabled entries by type
      // so we can match each device's source to its vendor API candidates.
      const enabledVendorApi = (snapshotSettings.vendorApi ?? []).filter(
        (record) => record?.isEnabled,
      );
      const enabledVendorApiByType = enabledVendorApi.reduce(
        (acc: Record<string, string[]>, record: PublicVendorApi) => {
          const id = record?.id;
          const type = record?.type;
          if (!id || !type) {
            return acc;
          }
          const idsForType = acc[type] ?? [];
          idsForType.push(id);
          acc[type] = idsForType;
          return acc;
        },
        {},
      );

      // Build SN -> vendor API ID mapping, but only when there is exactly one
      // enabled vendor API for the device source to avoid arbitrary picks.
      return devicesResponse.data.reduce((acc: VendorSettingsMap, row) => {
        if (isNullable(row.source) || isNullable(row.sn)) {
          return acc;
        }
        const sn = row.sn as NodeSn;
        const candidates = enabledVendorApiByType[row.source] ?? [];
        if (candidates.length === 1) {
          const [candidate] = candidates;
          if (candidate) {
            acc[sn] = candidate;
          }
        }
        return acc;
      }, {});
    },
  );

  const onRefreshDevices = useStableCallback(
    async (
      selectedDeviceSns: NodeSn[],
      continueDiscoveryEvent?: ContinueDiscoveryEvent,
    ) => {
      // tabs are being removed in UI, replaced by table bulk action, but we need
      // to know for subsequent workflow (retry in step 2) what operation we perform.
      setTabId('refreshDevices');
      const resolvedVendorSettingsMap =
        await resolveVendorSettingsMap(selectedDeviceSns);
      await onStartDiscovery(
        `/snapshots/${props.snapshotId}/devices`,
        selectedDeviceSns,
        continueDiscoveryEvent,
        resolvedVendorSettingsMap,
      );
    },
  );

  const onDeleteDevices = useStableCallback(
    async (selectedDeviceSns: NodeSn[]) => {
      // tabs are being removed in UI, replaced by table bulk action, but we need
      // to know for subsequent workflow (retry in step 2) what operation we perform.
      setTabId('deleteDevices');
      await withLoadingIndicator(setSendingDataState)(async () => {
        await api.request({
          url: `/snapshots/${props.snapshotId}/devices`,
          method: 'DELETE',
          data: selectedDeviceSns,
        });
        props.onClose?.();
      });
    },
  );

  const onBreadcrumbsStep1Click = useStableCallback(() => {
    setBreadcrumbsStep(1);
  });

  const onContinueDiscovery = useStableCallback(
    (event: ContinueDiscoveryEvent) => {
      if (
        tabId !== 'addDevices' &&
        event.notSelectedDeviceSns?.length === selectedSns.length
      ) {
        showNotification({
          type: 'warning',
          title: msg.snapshotCreation.noItemsToSendTitle,
          message: msg.snapshotCreation.noItemsToSendMessage,
        });
        return;
      }
      switch (tabId) {
        case 'addDevices':
          onInsertNetwork();
          break;
        case 'createSnapshot':
          if (event.notSelectedDeviceSns === undefined) {
            stumble('Missing not selected device IDs');
          }
          onCreateNewSnapshot(selectedSns, event);
          break;
        case 'refreshDevices':
          pipe(
            event,
            option.fromPredicate(
              (ev) =>
                ev.notSelectedDeviceSns !== undefined ||
                ev.acceptedDeviceSns !== undefined,
            ),
            option.match(
              () => {
                stumble('Missing not selected device IDs');
              },
              (ev) => {
                onRefreshDevices(selectedSns, ev);
              },
            ),
          );
          break;
        default:
          break;
      }
    },
  );

  const onSnapshotSettingsOpen = useStableCallback((settingsTab: string) => {
    dispatch(
      openSettings({
        disableRedirect: true,
        skipPasswordOverlay: true,
      }),
    );
    setSnapshotSettingsTab(settingsTab);
  });

  const onSnapshotSettingsClose = useStableCallback(() => {
    setSnapshotSettingsTab(null);
  });

  const onInsertNetwork = useStableCallback(async () => {
    await withLoadingIndicator(setSendingDataState)(async () => {
      try {
        await api.request({
          url: `/snapshots/${props.snapshotId}/devices`,
          method: 'POST',
          data: {
            ipList: insertNetworkFormik.values.ipList.ips,
            retryTimedOut: insertNetworkFormik.values.retryTimedOut,
            vendorApi: transformVendorsForRequest(vendors),
          },
        });
      } catch (e: any) {
        if (e.code === API_NOTHING_TO_DO && e.message !== undefined) {
          showNotification({ type: 'warning', message: e.message });
        } else if (e.code === API_INVALID_IP) {
          showFixSettings(e);
        } else {
          // Shows vendor API notification or default error message if it's general error.
          showNotification({ type: 'danger', message: e.message });
        }
      }
    });
  });

  const onFetchVendors = useStableCallback(async () => {
    await withLoadingIndicator(setLoadingVendorsState)(async () => {
      const vendorApiRequest = await api.request<ExistingVendor[]>({
        url: `/snapshots/${props.snapshotId}/available-vendor-settings`,
        method: 'GET',
      });
      setVendors(vendorApiRequest);
    });
  });

  return useMemo(
    () => ({
      breadcrumbsStep,
      insertNetworkFormik,
      loadDependencies,
      loadingVendorsState,
      failedData,
      onBreadcrumbsStep1Click,
      onContinueDiscovery,
      onCreateNewSnapshot,
      onDeleteDevices,
      onFetchVendors,
      onInsertNetwork,
      onRefreshDevices,
      onSnapshotSettingsClose,
      onSnapshotSettingsOpen,
      onSourceSelect,
      onTabChange,
      requestErrors,
      selectedSns,
      sendingDataState,
      snapshotSettingsTab,
      tabId,
      taskApiSourceMap,
      vendors,
      setVendors,
    }),
    [
      breadcrumbsStep,
      insertNetworkFormik,
      loadingVendorsState,
      failedData,
      requestErrors,
      selectedSns,
      sendingDataState,
      snapshotSettingsTab,
      tabId,
      taskApiSourceMap,
      vendors,
    ],
  );
};
